import { Box, Flex, Image, NugIcon, Typography } from '@iheartjane/reefer';
import type { TypographyProps } from '@iheartjane/reefer';
import type { CategoryTabStyle, MenuTab } from '@iheartjane/types';
import { sentenceCase } from '@iheartjane/utils';
import { clsx } from 'clsx';

import { MENU_CATEGORY_TABS } from './categoryTab.utils';

import styles from './categoryTab.module.css';

export interface CategoryTabProps {
  customLabel?: string;
  darkPrimaryColor?: boolean;
  name: MenuTab;
  // Used for skeleton previews in Biz Admin
  preview?: boolean;
  selected: boolean;
  storeState?: string;
  variant: CategoryTabStyle;
}

export function getCategoryTabLabel(
  name: MenuTab,
  customLabel?: string
): string {
  if (name === 'brand_specials') return 'Jane Gold';
  return (
    customLabel || sentenceCase(name === 'all-products' ? 'shop all' : name)
  );
}

const CategoryTabLabel = ({
  children,
  preview,
  ...props
}: { preview?: boolean } & TypographyProps) => {
  return preview ? (
    <Box
      as="p"
      width="72px"
      height="24px"
      background="grays-light"
      borderRadius="lg"
    />
  ) : (
    <Typography {...props}>{children}</Typography>
  );
};

export const CategoryTab = ({
  customLabel,
  darkPrimaryColor = true,
  name,
  preview,
  selected,
  storeState,
  variant,
}: CategoryTabProps) => {
  const isBrandSpecialsTab = name === 'brand_specials';
  const nameToRender = getCategoryTabLabel(name, customLabel);

  const isOhioFlower = name === 'flower' && storeState === 'Ohio';
  const Icon = isOhioFlower ? NugIcon : MENU_CATEGORY_TABS[name].Icon;

  const iconColor = selected
    ? isBrandSpecialsTab
      ? 'gold'
      : darkPrimaryColor
        ? 'grays-white'
        : 'grays-black'
    : 'grays-black';
  const backgroundColor = selected
    ? isBrandSpecialsTab && variant === 'icon'
      ? 'gold-light'
      : 'primary'
    : variant === 'icon'
      ? 'grays-ultralight'
      : 'grays-white';

  switch (variant) {
    case 'icon':
      return (
        <Flex
          flexDirection="column"
          alignItems="center"
          minWidth="82px"
          maxWidth="100px"
          gap={4}
        >
          <Flex
            background={backgroundColor}
            p={8}
            width="40px"
            borderRadius="lg"
            alignItems="center"
            justifyContent="center"
          >
            <Icon
              className="category_icon"
              color={iconColor}
              contrastCompliant={!isBrandSpecialsTab}
            />
          </Flex>
          <CategoryTabLabel
            variant={selected ? 'body-semibold' : 'body-regular'}
            color={selected ? 'grays-black' : 'grays-mid'}
            textAlign="center"
            preview={preview}
            width="100%"
            maxLines={2}
          >
            {nameToRender}
          </CategoryTabLabel>
        </Flex>
      );

    case 'pill':
      return (
        <Flex
          background={backgroundColor}
          px={16}
          py={12}
          gap={8}
          width="min-content"
          height="min-content"
          borderRadius="lg"
          border={selected ? undefined : 'transparent-black-ten'}
          alignItems="center"
          justifyContent="center"
        >
          <Box>
            <Icon
              className="category_icon"
              color={iconColor}
              contrastCompliant={!isBrandSpecialsTab}
            />
          </Box>
          <CategoryTabLabel
            variant={selected ? 'body-semibold' : 'body-regular'}
            color={
              selected
                ? darkPrimaryColor
                  ? 'grays-white'
                  : 'grays-black'
                : 'grays-mid'
            }
            whiteSpace="nowrap"
            preview={preview}
          >
            {nameToRender}
          </CategoryTabLabel>
        </Flex>
      );

    case 'tile':
      return (
        <Flex
          flexDirection="column"
          background={backgroundColor}
          px={8}
          py={4}
          gap={4}
          height="88px"
          minWidth="88px"
          maxWidth="141px"
          borderRadius="sm"
          border={selected ? undefined : 'transparent-black-ten'}
          alignItems="center"
          justifyContent="center"
        >
          <Flex
            maxHeight="40px"
            alignItems="center"
            justifyContent="center"
            style={{ flex: 1 }}
          >
            <Icon
              className="category_icon"
              color={iconColor}
              contrastCompliant={!isBrandSpecialsTab}
            />
          </Flex>
          <CategoryTabLabel
            variant={selected ? 'body-semibold' : 'body-regular'}
            color={
              selected
                ? darkPrimaryColor
                  ? 'grays-white'
                  : 'grays-black'
                : 'grays-mid'
            }
            textAlign="center"
            preview={preview}
            width="100%"
            maxLines={2}
          >
            {nameToRender}
          </CategoryTabLabel>
        </Flex>
      );

    case 'tab':
      return (
        <Flex flexDirection="column" width="min-content" gap={4} px={8} pt={12}>
          <CategoryTabLabel
            variant={selected ? 'body-semibold' : 'body-regular'}
            color={selected ? 'grays-black' : 'grays-mid'}
            whiteSpace="nowrap"
            preview={preview}
          >
            {nameToRender}
          </CategoryTabLabel>
          {selected && (
            <Flex
              className={styles['tab']}
              height="3px"
              alignSelf="stretch"
              background={darkPrimaryColor ? 'primary' : 'primary-dark'}
            />
          )}
        </Flex>
      );

    case 'image':
      return (
        <Flex
          className={clsx(styles['image'], {
            [styles['image_selected']]: selected,
          })}
          flexDirection="column"
          background="grays-white"
          px={16}
          py={4}
          gap={4}
          alignItems="center"
          justifyContent="center"
          border={
            selected
              ? darkPrimaryColor
                ? 'primary'
                : 'primary-dark'
              : 'transparent-black-ten'
          }
        >
          <Flex alignItems="center" justifyContent="center">
            {MENU_CATEGORY_TABS[name].image ? (
              <Image
                className="category_icon"
                width="100%"
                altText={`${nameToRender} image`}
                src={MENU_CATEGORY_TABS[name].image}
              />
            ) : (
              <Icon
                className={clsx(styles['image_icon'], 'category_icon')}
                size="md"
                color={MENU_CATEGORY_TABS[name].iconColor}
                contrastCompliant={!isBrandSpecialsTab}
              />
            )}
          </Flex>
          <CategoryTabLabel
            variant="body-semibold"
            textAlign="center"
            preview={preview}
            width="100%"
            maxLines={2}
          >
            {nameToRender}
          </CategoryTabLabel>
        </Flex>
      );
  }
};
